import { Button, Input } from "antd";
import React, { Component } from "react";
import { injectIntl } from "react-intl";

import MessageInput from "../MessageInput";
import MessageList from "../MessageList";
import SelectPrivacy from "../SelectPrivacy";
import SelectReceivers from "../SelectReceivers";

import "./MessageBoxContent.scss";

function GeneralMessageOptions({
  generalMessageOptions,
  chat,
  messageVisibilityOptions,
  closeGeneralMessageOptions,
  saveGeneralMessageOptions,
  newDescription,
  selectedUsers,
  formatMessage,
  onInputChange,
  onCategoryChange,
  onSelectedUsersChange,
}) {
  const chatConfig = window.Social.getChatConfig();

  const currentChatPrivacyElement = chat?.elements?.find(
    (elem) => elem.description === chatConfig.privacyElement
  );

  const currentChatHashElement = chat?.elements?.find(
    (elem) => elem.description === chatConfig.hashElementPrefix
  );

  const currentDraft = {
    privacy: currentChatPrivacyElement?.content || chatConfig.defaultPrivacy,
    category: currentChatHashElement?.content || chatConfig.defaultCategory,
  };

  const hasDescriptionChanged = chat.description !== newDescription;
  const hasRecipientsChanged =
    selectedUsers !== null &&
    JSON.stringify(chat.recipients.map((r) => r.id).sort()) !==
      JSON.stringify(
        Object.values(selectedUsers)
          .map((r) => r.id)
          .sort()
      );

  if (!generalMessageOptions) {
    return;
  }

  return (
    <div className="message-box-content edit">
      <div className="wrapper">
        <span className="option-title">
          {formatMessage({ id: "general.name" })}
        </span>

        <Input
          className="chat-name"
          value={newDescription}
          onChange={onInputChange}
          placeholder={
            chat.description || formatMessage({ id: "chat.optional" })
          }
        />

        <span className="option-title">
          {formatMessage({ id: "general.privacy" })}
        </span>

        <SelectPrivacy
          draft={currentDraft}
          messageVisibilityOptions={messageVisibilityOptions}
          disabled
          onCategoryChange={onCategoryChange}
        />

        <span className="option-title">
          {formatMessage({ id: "general.recipients" })}
        </span>

        <SelectReceivers
          recipients={chat.recipients}
          initialRecipients={chat.recipients
            .map((r) => r.user?.username || r.username)
            .filter(Boolean)}
          onSelectedUsersChange={onSelectedUsersChange}
        />
      </div>

      <div className="message-configuration-buttons">
        <Button
          className="message-configuration cancel"
          onClick={closeGeneralMessageOptions}
        >
          {formatMessage({ id: "general.cancel" })}
        </Button>

        <Button
          className="message-configuration apply"
          type="primary"
          disabled={!hasDescriptionChanged && !hasRecipientsChanged}
          onClick={saveGeneralMessageOptions}
        >
          {formatMessage({ id: "general.apply" })}
        </Button>
      </div>
    </div>
  );
}

function NewChatComponents({
  newChat,
  chat,
  newChatDescription,
  draft,
  profiles,
  messageVisibilityOptions,
  inputChatNamePlaceholder,
  onCategoryChange,
  onSelectedUsersChange,
  onChatTitleChange,
}) {
  if (!newChat) {
    return false;
  }

  return (
    <div className="new-chat">
      <SelectPrivacy
        chat={chat}
        draft={draft}
        newChat={newChat}
        messageVisibilityOptions={messageVisibilityOptions}
        onCategoryChange={onCategoryChange}
      />

      <SelectReceivers
        chat={chat}
        draft={draft}
        profiles={profiles}
        onSelectedUsersChange={onSelectedUsersChange}
      />

      <Input
        className="chat-name"
        value={newChatDescription}
        placeholder={inputChatNamePlaceholder}
        onChange={onChatTitleChange}
      />
    </div>
  );
}

function GetMessageList({
  chat,
  loading,
  messages,
  handleInfiniteOnLoad,
  hasMorePages,
  newMessage,
  setNewMessagesToScrollCount,
  setMessageListRef,
  isLastMessageFromCurrentUser,
  loadingMore,
  loadingActiveUsers,
}) {
  return (
    !chat.newChat && (
      <MessageList
        id={chat.id}
        chat={chat}
        loading={loading}
        list={messages}
        handleInfiniteOnLoad={handleInfiniteOnLoad}
        hasMorePages={hasMorePages}
        newMessage={newMessage}
        setNewMessagesToScrollCount={setNewMessagesToScrollCount}
        setMessageListRef={setMessageListRef}
        isLastMessageFromCurrentUser={isLastMessageFromCurrentUser}
        loadingMore={loadingMore}
        loadingActiveUsers={loadingActiveUsers}
      />
    )
  );
}

function NewMessageSpanScroller({
  newMessagesToScrollCount,
  scrollMessageListToBottom,
  intl,
}) {
  return (
    newMessagesToScrollCount > 0 && (
      <div className="new-message-anchor" onClick={scrollMessageListToBottom}>
        <span className="title">
          {intl.formatMessage(
            {
              id: "chat.newMessagesReceived",
            },
            {
              number: newMessagesToScrollCount,
              messagePlural:
                newMessagesToScrollCount > 1
                  ? intl.formatMessage({ id: "general.messagePlural" })
                  : intl.formatMessage({ id: "general.messageSingular" }),
            }
          )}
        </span>
      </div>
    )
  );
}

class MessageBoxContent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      newDescription: this.props.chat.description || "",
      newChatDescription: (this.props.draft && this.props.draft.title) || "",
      generalSelectedUsers: null,
    };
  }

  onInputChange(e) {
    const chatConfig = window.Social.getChatConfig();

    let title = e.target.value;
    if (title.length >= chatConfig.descriptionMaxLength) {
      title = title.slice(0, chatConfig.descriptionMaxLength);
    }

    this.setState({
      newDescription: title,
    });
  }

  saveGeneralMessageOptions() {
    const params = {
      description: this.state.newDescription,
    };

    if (this.state.generalSelectedUsers) {
      params.recipients = Object.values(this.state.generalSelectedUsers).map(
        (r) => ({
          id: r.id,
          user: {
            id: r.user?.id || r.id,
            username: r.user?.username || r.username,
          },
        })
      );
    }

    this.props.saveGeneralMessageOptions(params);
  }

  onGeneralSelectedUsersChange = (users) => {
    this.setState({
      generalSelectedUsers: users,
    });
  };

  onChatTitleChange(e) {
    const chatConfig = window.Social.getChatConfig();

    let title = e.target.value;

    if (title.length >= chatConfig.descriptionMaxLength) {
      title = title.slice(0, chatConfig.descriptionMaxLength);
    }

    this.setState(
      {
        newChatDescription: title,
      },
      this.props.onChatTitleChange(e)
    );
  }

  render() {
    const { formatMessage } = this.props.intl;

    if (this.props.collapsed) {
      return false;
    }

    if (this.props.generalMessageOptions) {
      return (
        <div className="message-box-content">
          <GeneralMessageOptions
            chat={this.props.chat}
            draft={this.props.draft}
            formatMessage={formatMessage}
            newDescription={this.state.newDescription}
            selectedUsers={this.state.generalSelectedUsers}
            generalMessageOptions={this.props.generalMessageOptions}
            messageVisibilityOptions={this.props.messageVisibilityOptions}
            closeGeneralMessageOptions={this.props.closeGeneralMessageOptions}
            saveGeneralMessageOptions={this.saveGeneralMessageOptions.bind(
              this
            )}
            onInputChange={this.onInputChange.bind(this)}
            onCategoryChange={this.props.onCategoryChange}
            onSelectedUsersChange={this.onGeneralSelectedUsersChange}
          />
        </div>
      );
    }

    return (
      <div
        className={`message-box-content ${
          this.props.chat.newChat ? "new-chat" : "chat"
        }`}
      >
        <NewChatComponents
          newChat={this.props.chat.newChat}
          chat={this.props.chat}
          draft={this.props.draft}
          profiles={this.props.profiles}
          newChatDescription={this.state.newChatDescription}
          messageVisibilityOptions={this.props.messageVisibilityOptions}
          inputChatNamePlaceholder={
            this.props.intl.messages["chat.optionalTitle"]
          }
          onChatTitleChange={this.onChatTitleChange.bind(this)}
          onCategoryChange={this.props.onCategoryChange}
          onSelectedUsersChange={this.props.onSelectedUsersChange}
        />

        <GetMessageList
          chat={this.props.chat}
          loading={this.props.loading}
          loadingActiveUsers={this.props.loadingActiveUsers}
          messages={this.props.messages}
          handleInfiniteOnLoad={this.props.handleInfiniteOnLoad}
          hasMorePages={this.props.hasMorePages}
          newMessage={this.props.newMessage}
          setNewMessagesToScrollCount={this.props.setNewMessagesToScrollCount}
          setMessageListRef={this.props.setMessageListRef}
          isLastMessageFromCurrentUser={this.props.isLastMessageFromCurrentUser}
          loadingMore={this.props.loadingMore}
        />

        <MessageInput
          newChat={this.props.chat.newChat}
          chat={this.props.chat}
          draft={this.props.draft}
          templateSettings={this.props.templateSettings}
          readOnly={this.props.waitingMessageSentConfirmation}
          chatState={this.props.chatState}
          initialState={this.props.draft && this.props.draft.message}
          canSendMessage={this.props.canSendMessage}
          waitingMessageSentConfirmation={
            this.props.waitingMessageSentConfirmation
          }
          sendMessage={this.props.sendMessage}
          flags={this.props.flags}
          profiles={this.props.profiles}
          loadingActiveUsers={this.props.loadingActiveUsers}
          onFocus={() => this.props.dismissNotification(this.props.chat.id)}
          onChange={this.props.onMessageInputChange}
          onFlagSelect={this.props.onFlagSelect}
        />

        <NewMessageSpanScroller
          newMessagesToScrollCount={this.props.newMessagesToScrollCount}
          scrollMessageListToBottom={this.props.scrollMessageListToBottom}
          intl={this.props.intl}
        />
      </div>
    );
  }
}

export default injectIntl(MessageBoxContent);
